import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import hiveService from '../services/hive';
import { workspaceApi } from '../services/api';
import MarkdownRenderer from '../components/MarkdownRenderer/MarkdownRenderer';
import TagInput from '../components/TagInput/TagInput';
import CommunitySelect from '../components/CommunitySelect/CommunitySelect';
import BeneficiariesModal from '../components/BeneficiariesModal/BeneficiariesModal';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import RichEditor from '../components/RichEditor';
import { buildArticleMetadataForTargetApp, getAvailableArticleTargetApps } from '../config/articleTargetApps';
import { htmlToMarkdown } from '../components/RichEditor/richEditorUtils';
import './CreateArticle.css';
import '../components/CreatePostModal/CreatePostModal.css';

const CreateTemplate = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user, isAuthenticated } = useAuth();
  const { defaultBeneficiaries } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const richEditorRef = useRef(null);

  const [templateId, setTemplateId] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [community, setCommunity] = useState('');
  const [tags, setTags] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);
  const [rewardOption, setRewardOption] = useState('50');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [beneficiariesTags, setBeneficiariesTags] = useState([]);
  const [beneficiariesInitialized, setBeneficiariesInitialized] = useState(false);
  const [showBeneficiariesModal, setShowBeneficiariesModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [coverImage, setCoverImage] = useState('');
  const [customCover, setCustomCover] = useState('');
  const [customCoverStatus, setCustomCoverStatus] = useState('idle');
  const [includeInBlog, setIncludeInBlog] = useState(false);
  const [bodyFormat, setBodyFormat] = useState('shortcode');
  const [detectedImages, setDetectedImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const availableTargetApps = useMemo(() => getAvailableArticleTargetApps(), []);
  const targetAppOptions = useMemo(
    () => availableTargetApps.filter((app) => typeof app?.jsonMetadata?.app === 'string' && app.jsonMetadata.app.trim() !== ''),
    [availableTargetApps]
  );
  const [targetApp, setTargetApp] = useState(() => (targetAppOptions[0]?.key || ''));

  useEffect(() => {
    if (!beneficiariesInitialized && defaultBeneficiaries && defaultBeneficiaries.length > 0) {
      setBeneficiariesTags(defaultBeneficiaries);
      setBeneficiariesInitialized(true);
    }
  }, [defaultBeneficiaries, beneficiariesInitialized]);

  useEffect(() => {
    if (targetAppOptions.length === 0) return;
    const hasSelected = targetAppOptions.some((app) => app.key === targetApp);
    if (!hasSelected) setTargetApp(targetAppOptions[0].key);
  }, [targetAppOptions, targetApp]);

  useEffect(() => {
    const imgs = Array.from(new Set(hiveService.extractImages(body || '')));
    setDetectedImages(imgs);
    if (!coverImage && imgs.length) setCoverImage(imgs[0]);
  }, [body, coverImage]);

  useEffect(() => {
    if (coverImage !== '__custom') { setCustomCoverStatus('idle'); return; }
    const url = customCover.trim();
    if (!url) { setCustomCoverStatus('idle'); return; }
    let cancelled = false;
    const image = new Image();
    setCustomCoverStatus('loading');
    const timeoutId = window.setTimeout(() => { if (!cancelled) setCustomCoverStatus('invalid'); }, 10000);
    image.onload = () => { if (cancelled) return; window.clearTimeout(timeoutId); setCustomCoverStatus('valid'); };
    image.onerror = () => { if (cancelled) return; window.clearTimeout(timeoutId); setCustomCoverStatus('invalid'); };
    image.src = url;
    return () => { cancelled = true; window.clearTimeout(timeoutId); image.onload = null; image.onerror = null; };
  }, [coverImage, customCover]);

  // Load template from URL param ?template=<id>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tplParam = params.get('template');
    if (!tplParam) return;
    const username = user?.username || localStorage.getItem('hive_username');
    setLoading(true);
    workspaceApi.getTemplate(tplParam, username)
      .then(data => {
        if (data?.template) {
          const t = data.template;
          setTitle(t.title || '');
          setBodyHtml(t.body_html || '');
          setBody(t.body || '');
          setCommunity(t.community || '');
          setTags(Array.isArray(t.tags) ? t.tags : []);
          setTargetApp(t.target_app || targetAppOptions[0]?.key || '');
          setBodyFormat(t.body_format || 'shortcode');
          setRewardOption(t.reward_option || '50');
          setBeneficiariesTags(Array.isArray(t.beneficiaries) ? t.beneficiaries : []);
          if (t.cover_image) setCoverImage(t.cover_image);
          setIncludeInBlog(t.include_in_blog || false);
          setTemplateId(Number(tplParam));
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCommunities = useCallback(async () => {
    try {
      setCommunitiesLoading(true);
      const uname = user?.username || localStorage.getItem('hive_username');
      let url = '/api/feed/communities?sort=rank&limit=50';
      if (uname) url = `/api/feed/communities/mine?username=${encodeURIComponent(uname)}&limit=1000`;
      const res = await fetch(url);
      const data = await res.json();
      setCommunities(data.communities || []);
    } catch (err) {
      console.error('Failed to load communities', err);
    } finally {
      setCommunitiesLoading(false);
    }
  }, [user?.username]);

  useEffect(() => { loadCommunities(); }, [loadCommunities]);

  if (!isAuthenticated) return <Navigate to="/" replace />;

  const handleBodyChange = (html) => {
    setBodyHtml(html);
    setBody(htmlToMarkdown(html));
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError('');
    if (!title.trim()) return setError('Template title is required');

    const username = user?.username || localStorage.getItem('hive_username');
    const normalizedBody = bodyHtml && bodyHtml.trim() ? htmlToMarkdown(bodyHtml) : body;

    const images = Array.from(new Set(hiveService.extractImages(normalizedBody || '')));
    let finalCover = coverImage === '__custom' ? customCover.trim() : coverImage;
    if (finalCover && !images.includes(finalCover)) images.unshift(finalCover);

    const metadata = buildArticleMetadataForTargetApp(targetApp, {
      tags: [...tags],
      format: 'markdown',
    });
    if (bodyFormat === 'shortcode') metadata.shortcode = true;

    const payload = {
      author: username,
      title,
      body: normalizedBody,
      body_html: bodyHtml,
      json_metadata: metadata,
      community,
      tags,
      target_app: targetApp,
      body_format: bodyFormat,
      reward_option: rewardOption,
      beneficiaries: beneficiariesTags,
      cover_image: finalCover || null,
      include_in_blog: includeInBlog,
    };

    try {
      setSaving(true);
      if (templateId) {
        await workspaceApi.updateTemplate(templateId, payload);
      } else {
        const result = await workspaceApi.createTemplate(payload);
        if (result?.template?.id) setTemplateId(result.template.id);
      }
      navigate('/create-article/templates');
    } catch (err) {
      setError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <SidebarHeader title="Template" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <div className="create-article-page"><p style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading template…</p></div>
      </>
    );
  }

  return (
    <>
      <SidebarHeader
        title={templateId ? 'Edit Template' : 'New Template'}
        onOpenLeftSidebar={onOpenLeftSidebar}
        onOpenRightSidebar={onOpenRightSidebar}
      />
      <div className="create-article-page">
        <div className="page-header">
          <h1>{templateId ? 'Edit Template' : 'New Template'}</h1>
          <p className="subtitle">Templates are saved permanently and can be reused to start new articles.</p>
        </div>

        <form className="post-form article-page-form" onSubmit={handleSave}>
          <div className="form-group">
            <input
              className="title-input"
              placeholder="Template title (also used as template name)…"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
            />
          </div>

          <div className="form-group">
            <div className="md-toolbar">
              <span className="md-charcount">{body.length} chars</span>
            </div>
            <RichEditor
              ref={richEditorRef}
              value={bodyHtml}
              onChange={handleBodyChange}
              placeholder="Write template content…"
              maxLength={null}
              toolbar={true}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Community</label>
            <CommunitySelect
              communities={communities}
              value={community}
              onChange={setCommunity}
              placeholder="Select a community…"
              loading={communitiesLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Tags</label>
            <TagInput tags={tags} onChange={setTags} placeholder="e.g. technology, tutorial" />
          </div>

          <button
            type="button"
            className="secondary-btn advanced-btn"
            onClick={() => setShowAdvanced(v => !v)}
            style={{ marginBottom: 12 }}
          >
            {showAdvanced ? 'Hide advanced options' : 'Show advanced options'}
          </button>

          {showAdvanced && (
            <>
              <div className="form-group">
                <label className="form-label">Cover image</label>
                <div className="cover-image-picker">
                  <button type="button" className={`cover-choice-btn ${coverImage === '' ? 'selected' : ''}`} onClick={() => { setCoverImage(''); setCustomCover(''); }}>No cover image</button>
                  <button type="button" className={`cover-choice-btn ${coverImage === '__custom' ? 'selected' : ''}`} onClick={() => setCoverImage('__custom')}>Custom image URL</button>
                </div>
                {coverImage === '__custom' && (
                  <div className="custom-cover-input-wrap">
                    <input type="text" className="title-input" placeholder="https://…" value={customCover} onChange={e => setCustomCover(e.target.value)} style={{ marginTop: 8 }} />
                    {customCoverStatus === 'loading' && <div className="cover-status cover-status-loading">Checking…</div>}
                    {customCoverStatus === 'valid' && <div className="cover-status cover-status-valid">Valid.</div>}
                    {customCoverStatus === 'invalid' && <div className="cover-status cover-status-invalid">Could not load image.</div>}
                  </div>
                )}
                {detectedImages.length > 0 && (
                  <div className="cover-thumbnails-grid">
                    {detectedImages.map((url, i) => (
                      <button key={`${url}-${i}`} type="button" className={`cover-thumb-btn ${coverImage === url ? 'selected' : ''}`} onClick={() => { setCoverImage(url); setCustomCover(''); }}>
                        <img src={url} alt={`Cover option ${i + 1}`} loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group include-blog-group">
                <label className="form-label">
                  <span>Include in my blog</span>
                  <input type="checkbox" checked={includeInBlog} onChange={e => setIncludeInBlog(e.target.checked)} />
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">Reward distribution</label>
                <select className="tags-input" value={rewardOption} onChange={e => setRewardOption(e.target.value)}>
                  <option value="50">50% HBD and 50% HP</option>
                  <option value="100">100% Hive Power</option>
                  <option value="-3">Use @reward.app</option>
                  <option value="-4">Use @commentrewarder</option>
                  <option value="-1">Burn</option>
                  <option value="-2">Donate to Hive Fund</option>
                  <option value="0">Decline</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Beneficiaries</label>
                <button type="button" className="secondary-btn blwh-btn" onClick={() => setShowBeneficiariesModal(true)}>Edit beneficiaries</button>
                {beneficiariesTags.length > 0 && (
                  <div className="benef-summary">
                    {beneficiariesTags.map(t => { const [a, w] = t.split('='); return `${a}=${w}%`; }).join(', ')}
                  </div>
                )}
              </div>
              <BeneficiariesModal
                isOpen={showBeneficiariesModal}
                onClose={() => setShowBeneficiariesModal(false)}
                initialTags={beneficiariesTags}
                onSave={setBeneficiariesTags}
              />

              {targetAppOptions.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Target app</label>
                  <select className="tags-input" value={targetApp} onChange={e => setTargetApp(e.target.value)}>
                    {targetAppOptions.map(app => <option key={app.key} value={app.key}>{app.label}</option>)}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Body format</label>
                <select className="tags-input" value={bodyFormat} onChange={e => setBodyFormat(e.target.value)}>
                  <option value="markdown">Markdown — compatible with all Hive frontends</option>
                  <option value="shortcode">Shortcode — only rendered correctly by this app</option>
                </select>
              </div>
            </>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" className="secondary-btn preview-form-btn" onClick={() => setPreviewOpen(true)}>Preview</button>
            <div style={{ flex: 1 }} />
            <button type="button" className="cancel-form-btn" onClick={() => navigate('/create-article/templates')}>Cancel</button>
            <button type="submit" className="submit-form-btn" disabled={saving}>{saving ? 'Saving…' : 'Save Template'}</button>
          </div>
        </form>

        {previewOpen && (
          <div className="preview-backdrop" onClick={() => setPreviewOpen(false)}>
            <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
              <button className="preview-close-btn" onClick={() => setPreviewOpen(false)}>×</button>
              <div className="preview-content">
                <div className="post-preview-header">
                  <h1>{title || 'Untitled'}</h1>
                </div>
                <div className="preview-body">
                  <MarkdownRenderer content={body || ''} />
                </div>
                <div className="preview-actions">
                  <button className="secondary-btn action-close-preview-btn" onClick={() => setPreviewOpen(false)}>Keep editing</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CreateTemplate;
